// The Actions page as a WORKSPACE: one read-only, normalized view over
// several canonical record types, not a second copy of any of them.
//
// Today that's canonical Actions (manual + every system automation) and
// Initiative Tasks. A WorkItem is built by READING an existing Action or
// Task — nothing here ever writes, and nothing here is itself persisted.
// There is exactly one canonical representation of an Initiative Task (the
// `tasks` doc) and exactly one of an Action (the `actions` doc); this file
// only reshapes them into one shared shape the Actions page can list, sort,
// and filter uniformly, so a future work source (Goal Tasks, some other
// automation) only needs one more `xToWorkItem()` function here — never new
// per-source branches scattered through the page.
//
// Deliberately pure (no Firestore, no React) — see lib/actions.ts's own
// header note for the same convention. Anything that depends on the
// CURRENT VIEWER (assigned-to-me, created-by-me) is composed by the caller
// from lib/visibility.ts's existing checks, not baked in here — a WorkItem
// is the same regardless of who's looking at it.

import { actionIsDueSoon, actionIsOverdue, actionTypeKey, fmtDate, type ActionTypeFilterValue, ACTION_TYPE_LABELS, ACTION_TYPE_FILTER_ORDER } from './actions.ts';
import { actionIsResolved, statusIsComplete, type Action, type ActionPriority, type Contact, type Goal, type Initiative, type StatusRecord, type Task, type Unit } from '../types.ts';

/** Moved here from components/StatusBadge.tsx (which now imports it back)
 *  so it's reachable from a pure, JSX-free module — a .tsx file can't be
 *  parsed by Node's plain type-stripping (no JSX support), which this
 *  file's own regression tests rely on (see
 *  scripts/test-actions-workitems.ts). Also de-duplicates Dashboard.tsx's
 *  near-identical local `dueSoon()` — see taskIsDueSoon below, same
 *  7-day-window logic, now defined once. */
export function taskIsOverdue(task: Pick<Task, 'dueDate'>, isComplete: boolean): boolean {
  if (!task.dueDate || isComplete) return false;
  return task.dueDate < new Date().toISOString().slice(0, 10);
}

export type WorkItemTypeKey = ActionTypeFilterValue | 'initiativeTask';

/** Extends ACTION_TYPE_LABELS (Actions-only) with the one work-item type
 *  that isn't a real Action at all. 'Initiative Task' is a workspace-level
 *  concept — see the file header — not a value ever stored on an Action
 *  doc's sourceType. */
export const WORK_ITEM_TYPE_LABELS: Record<WorkItemTypeKey, string> = {
  ...ACTION_TYPE_LABELS,
  initiativeTask: 'Initiative Task',
};

/** Matches the user-facing order requested for the Type filter: Manual,
 *  then Initiative Task, then the system automations. */
export const WORK_ITEM_TYPE_FILTER_ORDER: WorkItemTypeKey[] = [
  'manual',
  'initiativeTask',
  ...ACTION_TYPE_FILTER_ORDER.filter((t) => t !== 'manual'),
];

export function workItemTypeLabel(key: WorkItemTypeKey | string): string {
  return (WORK_ITEM_TYPE_LABELS as Record<string, string>)[key] ?? key;
}

/** Same 7-day "Due Soon" window as actionIsDueSoon (lib/actions.ts) —
 *  applied to a Task's own dueDate/isComplete rather than re-derived, so
 *  Initiative Tasks never get a different threshold than canonical
 *  Actions (the user's own explicit ask). Previously duplicated as a local
 *  `dueSoon()` in Dashboard.tsx — consolidated here, defined once. */
export function taskIsDueSoon(task: Pick<Task, 'dueDate'>, isComplete: boolean, withinDays = 7): boolean {
  if (!task.dueDate || isComplete) return false;
  if (taskIsOverdue(task, isComplete)) return false;
  const now = new Date();
  const soon = new Date();
  soon.setDate(now.getDate() + withinDays);
  const d = new Date(task.dueDate + 'T00:00:00');
  return d >= now && d <= soon;
}

export function taskIsUnassigned(task: Pick<Task, 'assignedPosition' | 'assignedContactIds' | 'assignedPersonIds'>): boolean {
  return !task.assignedPosition
    && !(task.assignedContactIds && task.assignedContactIds.length > 0)
    && !(task.assignedPersonIds && task.assignedPersonIds.length > 0);
}

/** The one normalized shape the Actions page lists/sorts/filters. Carries
 *  the original record (`action`/`task`) so the UI can still do
 *  kind-specific things (open the Action drawer; navigate to the Task's
 *  Initiative) without this file needing to know about routing or React
 *  state — normalization and navigation are different concerns. */
export interface WorkItem {
  key: string; // 'action:<id>' | 'task:<id>' — unique across both kinds
  kind: 'action' | 'initiativeTask';
  typeKey: WorkItemTypeKey;
  title: string;
  ref?: string;
  statusLabel: string;
  isResolved: boolean;
  /** null = no canonical priority exists for this work item (every
   *  Initiative Task today — Task has no priority field at all). Never a
   *  manufactured default; see the Priority-filter behavior below. */
  priority: ActionPriority | null;
  dueDate: string | null;
  isOverdue: boolean;
  isDueSoon: boolean;
  /** Only ever true for an Initiative Task — a canonical Action always has
   *  exactly one accountable owner by construction (Action.ownerContactId
   *  is required), so this is always false for kind: 'action'. */
  isUnassigned: boolean;
  districtId: string;
  createdBy: string | null;
  /** A short "this came from X" label — the parent Initiative's name for
   *  a Task; null for an Action (whose own relatedBadges() already covers
   *  unit/goal/initiative links with its own, richer multi-badge display —
   *  see Actions.tsx). */
  relatedLabel: string | null;
  action: Action | null;
  task: Task | null;
}

export function actionToWorkItem(a: Action): WorkItem {
  return {
    key: `action:${a.id}`,
    kind: 'action',
    typeKey: actionTypeKey(a) as WorkItemTypeKey,
    title: a.title,
    ref: a.ref,
    statusLabel: a.status,
    isResolved: actionIsResolved(a.status),
    priority: a.priority,
    dueDate: a.dueDate ?? null,
    isOverdue: actionIsOverdue(a),
    isDueSoon: actionIsDueSoon(a),
    isUnassigned: false,
    districtId: a.districtId,
    createdBy: a.createdBy ?? null,
    relatedLabel: null,
    action: a,
    task: null,
  };
}

export function initiativeTaskToWorkItem(
  t: Task,
  initiative: Initiative | undefined,
  taskStatuses: StatusRecord[],
): WorkItem {
  const isResolved = statusIsComplete(taskStatuses, t.status);
  const statusRecord = taskStatuses.find((s) => s.id === t.status);
  return {
    key: `task:${t.id}`,
    kind: 'initiativeTask',
    typeKey: 'initiativeTask',
    title: t.title,
    ref: t.ref,
    statusLabel: statusRecord?.name ?? 'Unknown',
    isResolved,
    priority: null,
    dueDate: t.dueDate ?? null,
    isOverdue: taskIsOverdue(t, isResolved),
    isDueSoon: taskIsDueSoon(t, isResolved),
    isUnassigned: taskIsUnassigned(t),
    districtId: t.districtId,
    createdBy: t.createdBy ?? null,
    relatedLabel: initiative?.name ?? null,
    action: null,
    task: t,
  };
}

/** Where clicking a WorkItem row should take the user — a canonical Action
 *  opens the existing in-page Action drawer; an Initiative Task navigates
 *  to its canonical home in Initiatives (via the SAME `?task=<id>` deep
 *  link Initiatives.tsx already opens a task-assignment-email link with —
 *  see that file's own `searchParams.get('task')` effect). Never a
 *  fake/generated Action detail record for a Task — this is the ONE
 *  place a Task can be edited (status included), so there is nothing to
 *  keep in sync. Pure/testable — Actions.tsx wires the actual
 *  setDetail()/navigate() calls to whichever branch this returns. */
export type WorkItemClickTarget = { kind: 'openAction'; action: Action } | { kind: 'navigate'; path: string };
export function workItemClickTarget(w: WorkItem): WorkItemClickTarget {
  if (w.kind === 'action') return { kind: 'openAction', action: w.action as Action };
  return { kind: 'navigate', path: `/initiatives?task=${w.task!.id}` };
}

/** Same ordering as lib/actions.ts's compareActions (overdue first, then
 *  soonest-due-first with undated last, then priority critical-first) —
 *  reimplemented against WorkItem's own precomputed fields rather than
 *  reused directly, since compareActions reads Action-specific fields
 *  (status/priority as required, non-nullable). A work item with no
 *  priority (every Initiative Task today) sorts after every real
 *  priority, never assigned a fake rank to make it comparable. */
const PRIORITY_RANK: Record<ActionPriority, number> = { critical: 0, high: 1, normal: 2, low: 3 };
export function compareWorkItems(a: WorkItem, b: WorkItem): number {
  if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
  const aDue = a.dueDate ?? '9999-99-99';
  const bDue = b.dueDate ?? '9999-99-99';
  if (aDue !== bDue) return aDue.localeCompare(bDue);
  const aRank = a.priority ? PRIORITY_RANK[a.priority] : 4;
  const bRank = b.priority ? PRIORITY_RANK[b.priority] : 4;
  return aRank - bRank;
}

/** Same phrasing/thresholds as lib/actions.ts's actionDueDateDisplay,
 *  driven by WorkItem's own precomputed isResolved/dueDate rather than an
 *  Action's status string — kept separate rather than widening that
 *  function's signature, since ActionDrawer still calls it directly
 *  against a raw Action. */
export function workItemDueDateDisplay(w: Pick<WorkItem, 'dueDate' | 'isResolved'>): { text: string; context: string | null } {
  if (!w.dueDate) return { text: '—', context: null };
  const text = fmtDate(w.dueDate);
  if (w.isResolved) return { text, context: null };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(w.dueDate + 'T00:00:00');
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);

  if (days < 0) return { text, context: `Overdue ${-days} day${-days === 1 ? '' : 's'}` };
  if (days === 0) return { text, context: 'Due today' };
  if (days <= 7) return { text, context: `Due in ${days} day${days === 1 ? '' : 's'}` };
  return { text, context: null };
}

/** Same "◉ Position" / names / "Unassigned" convention already used by
 *  GoalsTasks.tsx/Dashboard.tsx for a task's assignee — reused here as
 *  plain text (no icon glyph) for the Actions table's Owner column. */
export function taskOwnerLabel(
  task: Pick<Task, 'assignedPosition' | 'assignedContactIds' | 'assignedPersonIds'>,
  contacts: Pick<Contact, 'id' | 'name'>[],
  /** Person names (the canonical assignees). When given, Person-keyed assignees are named from here. */
  personName?: (personId: string) => string | null,
): string {
  if (task.assignedPosition) return task.assignedPosition;
  if (task.assignedPersonIds && task.assignedPersonIds.length > 0) {
    const names = task.assignedPersonIds.map((id) => personName?.(id)).filter(Boolean) as string[];
    return names.length ? names.join(', ') : `${task.assignedPersonIds.length} assigned`;
  }
  const ids = task.assignedContactIds ?? [];
  if (ids.length === 0) return 'Unassigned';
  const names = ids.map((id) => contacts.find((c) => c.id === id)?.name).filter(Boolean) as string[];
  return names.length ? names.join(', ') : `${ids.length} assigned`;
}

/** An Action's related-unit/goal/initiative names, for the table's own
 *  Related-column badges — moved here (unchanged) from Actions.tsx so the
 *  same logic can also feed workItemSearchText below without duplicating
 *  it in two places. */
export function relatedBadgesForAction(
  a: Pick<Action, 'relatedUnitId' | 'relatedUnitIds' | 'relatedGoalId' | 'relatedInitiativeId'>,
  units: Pick<Unit, 'id' | 'name'>[],
  goals: Pick<Goal, 'id' | 'title'>[],
  initiatives: Pick<Initiative, 'id' | 'name'>[],
): string[] {
  const badges: string[] = [];
  const unitIds = a.relatedUnitIds?.length ? a.relatedUnitIds : a.relatedUnitId ? [a.relatedUnitId] : [];
  for (const id of unitIds) badges.push(units.find((u) => u.id === id)?.name ?? 'Unit');
  if (a.relatedGoalId) badges.push(goals.find((g) => g.id === a.relatedGoalId)?.title ?? 'Goal');
  if (a.relatedInitiativeId) {
    badges.push(initiatives.find((i) => i.id === a.relatedInitiativeId)?.name ?? 'Initiative');
  }
  return badges;
}

/** Search-index text for one WorkItem — Actions.tsx's Search field matches
 *  a (trimmed, lowercased) query against this. Included: title, the
 *  human-readable ref badge (e.g. "ACT-014", already shown on-screen next
 *  to the title — a user-facing identifier, not an internal Firestore doc
 *  id), owner/assignee, participants (an Action only — a Task's single
 *  "owner" already covers everyone assigned via taskOwnerLabel), the
 *  description, and related unit/goal/initiative names (an Action) or the
 *  parent Initiative's name (a Task). Deliberately excludes: status/
 *  priority/type labels (already their own dedicated filters directly
 *  above the search box, so duplicating them here would just be noise) and
 *  createdBy/raw ids (an email isn't what a user means by "search", and
 *  "created by me" already has its own tab). Lowercased once here so every
 *  caller gets a normalized haystack without having to remember to do it
 *  themselves. */
export function workItemSearchText(
  w: WorkItem,
  ctx: {
    contactName: (id: string | null | undefined) => string | null;
    /** Person-keyed names (owner / participants / assignees), when available. */
    personName?: (id: string | null | undefined) => string | null;
    contacts: Pick<Contact, 'id' | 'name'>[];
    units: Pick<Unit, 'id' | 'name'>[];
    goals: Pick<Goal, 'id' | 'title'>[];
    initiatives: Pick<Initiative, 'id' | 'name'>[];
  },
): string {
  const parts: (string | null | undefined)[] = [w.title, w.ref];
  if (w.kind === 'action') {
    const a = w.action!;
    parts.push(a.ownerPersonId && ctx.personName ? ctx.personName(a.ownerPersonId) : ctx.contactName(a.ownerContactId), a.description);
    if (a.participantPersonIds && ctx.personName) for (const pid of a.participantPersonIds) parts.push(ctx.personName(pid));
    else for (const pid of a.participantContactIds) parts.push(ctx.contactName(pid));
    parts.push(...relatedBadgesForAction(a, ctx.units, ctx.goals, ctx.initiatives));
  } else {
    const t = w.task!;
    parts.push(taskOwnerLabel(t, ctx.contacts, ctx.personName ? (id) => ctx.personName!(id) : undefined), t.description, w.relatedLabel);
  }
  return parts.filter(Boolean).join(' ').toLowerCase();
}
